import React, { useState, useEffect, useCallback, memo } from 'react';
import { Plus, Trash2, Check, X, AlertTriangle } from 'lucide-react';
import { BottomSheet } from '../ui/BottomSheet';

const CATEGORIES = [
  { id: 'vital',    name: 'Vital',     color: '#EF4444', bg: 'rgba(239,68,68,0.12)',    icon: '❤️' },
  { id: 'work',     name: 'Travail',   color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',   icon: '💼' },
  { id: 'health',   name: 'Santé',     color: '#10B981', bg: 'rgba(16,185,129,0.12)',   icon: '🏃' },
  { id: 'personal', name: 'Personnel', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',   icon: '🏠' },
  { id: 'social',   name: 'Social',    color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)',   icon: '👥' },
  { id: 'admin',    name: 'Admin',     color: '#06B6D4', bg: 'rgba(6,182,212,0.12)',    icon: '📋' },
];

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

/**
 * EventEditorSheet - Bottom sheet for editing/creating agenda events
 */
const EventEditorSheet = memo(function EventEditorSheet({
  isOpen,
  event,          // null = new event
  onClose,
  onSave,         // (updatedEvent) => void
  onDelete,       // (eventId) => void
}) {
  const isNew = !event?.id;

  const [title, setTitle]         = useState('');
  const [date, setDate]           = useState('');
  const [start, setStart]         = useState('09:00');
  const [end, setEnd]             = useState('10:00');
  const [category, setCategory]   = useState('personal');
  const [description, setDescription] = useState('');
  const [subTasks, setSubTasks]   = useState([]);
  const [newSubTask, setNewSubTask] = useState('');
  const [titleError, setTitleError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Sync form fields when event changes
  useEffect(() => {
    if (!isOpen) return;
    if (event) {
      setTitle(event.title || '');
      setDate(event.date || new Date().toISOString().split('T')[0]);
      setStart(event.start || '09:00');
      setEnd(event.end || '10:00');
      setCategory(event.category || 'personal');
      setDescription(event.description || '');
      setSubTasks(event.subTasks ? [...event.subTasks] : []);
    } else {
      setTitle('');
      setDate(new Date().toISOString().split('T')[0]);
      setStart('09:00');
      setEnd('10:00');
      setCategory('personal');
      setDescription('');
      setSubTasks([]);
    }
    setNewSubTask('');
    setTitleError(false);
    setShowDeleteConfirm(false);
  }, [event, isOpen]);

  const handleSave = useCallback(() => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }
    onSave({
      ...(event || {}),
      id: event?.id || `event-${uid()}`,
      title: title.trim(),
      date,
      start,
      end,
      category,
      description: description.trim(),
      subTasks,
    });
  }, [title, date, start, end, category, description, subTasks, event, onSave]);

  const handleDelete = useCallback(() => {
    if (!event?.id) return;
    onDelete(event.id);
    setShowDeleteConfirm(false);
  }, [event, onDelete]);

  const addSubTask = useCallback(() => {
    if (!newSubTask.trim()) return;
    setSubTasks(prev => [...prev, { id: uid(), text: newSubTask.trim(), completed: false }]);
    setNewSubTask('');
  }, [newSubTask]);

  const toggleSubTask = useCallback((id) => {
    setSubTasks(prev => prev.map(st =>
      st.id === id ? { ...st, completed: !st.completed } : st
    ));
  }, []);

  const removeSubTask = useCallback((id) => {
    setSubTasks(prev => prev.filter(st => st.id !== id));
  }, []);

  const handleTitleChange = useCallback((e) => {
    setTitle(e.target.value);
    if (e.target.value.trim()) setTitleError(false);
  }, []);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={isNew ? 'Nouvel événement' : 'Modifier'}
      size="large"
    >
      <div className="px-4 pb-8 space-y-5">
        {/* Title */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Titre
          </label>
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            placeholder="Nom de l'événement..."
            autoFocus={isOpen}
            className={`w-full mt-2 px-4 py-3 bg-zinc-800 border rounded-xl text-white placeholder:text-zinc-500 focus:outline-none transition-colors ${
              titleError ? 'border-red-500' : 'border-zinc-700 focus:border-blue-500'
            }`}
          />
          {titleError && (
            <p className="mt-1 text-xs text-red-400">Le titre est requis</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full mt-2 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Time range */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
              Début
            </label>
            <input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-end pb-3 text-zinc-600 font-medium">→</div>
          <div className="flex-1">
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
              Fin
            </label>
            <input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Catégorie
          </label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                  category === cat.id ? 'ring-2 ring-white/40 scale-105' : 'opacity-60 hover:opacity-80'
                }`}
                style={{ backgroundColor: cat.bg, color: cat.color }}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Notes
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Notes, contexte, lien..."
            rows={2}
            className="w-full mt-2 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500 resize-none"
          />
        </div>

        {/* Subtasks */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Sous-tâches
          </label>

          {subTasks.length > 0 && (
            <div className="mt-2 space-y-2">
              {subTasks.map((st) => (
                <div
                  key={st.id}
                  className="flex items-center gap-3 px-3 py-2.5 bg-zinc-800/60 rounded-xl"
                >
                  <button
                    onClick={() => toggleSubTask(st.id)}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      st.completed
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'border-zinc-600'
                    }`}
                  >
                    {st.completed && <Check className="w-3 h-3" />}
                  </button>
                  <span className={`flex-1 text-sm ${st.completed ? 'line-through text-zinc-500' : 'text-zinc-200'}`}>
                    {st.text}
                  </span>
                  <button
                    onClick={() => removeSubTask(st.id)}
                    className="p-1 text-zinc-600 hover:text-zinc-400"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={newSubTask}
              onChange={(e) => setNewSubTask(e.target.value)}
              placeholder="Ajouter une étape..."
              className="flex-1 px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && addSubTask()}
            />
            <button
              onClick={addSubTask}
              className="px-3 py-2.5 bg-zinc-700 rounded-xl text-white hover:bg-zinc-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Delete confirm inline */}
        {showDeleteConfirm && (
          <div className="p-4 bg-red-950/40 border border-red-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <p className="text-sm font-medium text-red-300">Supprimer cet événement ?</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 bg-zinc-800 text-zinc-300 text-sm font-medium rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg"
              >
                Supprimer
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          {!isNew && !showDeleteConfirm && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-3.5 bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 transition-colors"
              title="Supprimer"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 py-3.5 bg-zinc-800 text-zinc-300 font-medium rounded-xl hover:bg-zinc-700 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-500 transition-colors"
          >
            {isNew ? 'Créer' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
});

export { EventEditorSheet };
export default EventEditorSheet;
